import { useState, useEffect } from 'react';
import { client } from '@/api/client';
import { toast } from 'sonner';
import { CheckCircle2, ChevronRight, ChevronLeft, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { findVinConflict } from '@/lib/vinCheck';
import { numOrDefault } from '@/lib/invoiceHelpers';
import { jsPDF } from 'jspdf';

import StepDriverType from '@/components/onboarding/StepDriverType';
import StepPersonalInfo from '@/components/onboarding/StepPersonalInfo';
import StepCDLMedical from '@/components/onboarding/StepCDLMedical';
import StepDrivingRecord from '@/components/onboarding/StepDrivingRecord';
import StepEmploymentHistory, { emptyEmployer } from '@/components/onboarding/StepEmploymentHistory';
import StepEquipmentBanking from '@/components/onboarding/StepEquipmentBanking';
import PolicyConsentStep from '@/components/onboarding/PolicyConsentStep';
import StepAppSignature from '@/components/onboarding/StepAppSignature';
import StepTaxForms from '@/components/onboarding/StepTaxForms';

const STEPS = [
  { id: 1, label: 'Application Type' },
  { id: 2, label: 'Personal Info' },
  { id: 3, label: 'CDL & Medical' },
  { id: 4, label: 'Driving Record' },
  { id: 5, label: 'Employment' },
  { id: 6, label: 'Equipment & Banking' },
  { id: 7, label: 'Tax Forms' },
  { id: 8, label: 'Policies & Consents' },
  { id: 9, label: 'Signature' },
];

const initialForm = {
  // Personal
  first_name: '', last_name: '', company_name: '', dob: '', ein: '', phone: '', email: '',
  address_street: '', address_city: '', address_state: '', address_zip: '',
  prev_address_street: '', prev_address_city: '', prev_address_state: '', prev_address_zip: '',
  twic_card: 'No', passport: 'No',
  // CDL
  lic_first: '', lic_last: '', cdl_number: '', cdl_state: '', cdl_class: 'US Class A',
  cdl_endorsements: '', cdl_issued_date: '', cdl_expiration_date: '', cdl_document_url: '',
  // Medical
  med_card_issue_date: '', med_card_expiration_date: '',
  med_card_examiner_name: '', med_card_registry_number: '', med_card_document_url: '',
  // Driving Record
  accidents: 'No', violations: 'No', denied_license: 'No', revoked_license: 'No',
  forfeiture_explain: '', drug_test_positive: 'No',
  // Equipment & Banking
  equipment_preference: '', truck_type: 'Dry Van', truck_info: '', trailer_info: '',
  truck_year: '', truck_make: '', truck_model: '',
  truck_vin: '', license_plate: '', plate_state: '', plate_expiration: '',
  registration_expiration: '', annual_inspection_due: '', current_odometer: '',
  dispatch_service: false,
  bank_routing_number: '', bank_account_number: '', void_check_url: '',
  // Tax Forms -- W-4 (Company Driver) or W-9 (Owner Operator)
  w4_ssn: '', w4_filing_status: '', w4_multiple_jobs: false,
  w4_dependents_qualifying: '', w4_dependents_other: '', w4_other_credits: '',
  w4_other_income: '', w4_deductions: '', w4_extra_withholding: '',
  w4_exempt: false, w4_signature: '',
  w9_business_name: '', w9_tax_classification: '', w9_llc_classification: '',
  w9_tin_type: 'SSN', w9_tin: '', w9_signature: '',
  // Signature
  app_signature: '', app_sign_date: new Date().toISOString().split('T')[0],
};

const DRAFT_KEY = 'onboarding_draft';
const DRAFT_VERSION = 2; // bump to invalidate old drafts

export default function Onboarding() {
  // Restore draft from localStorage — ignore drafts from older versions
  const savedDraft = (() => {
    try {
      const d = JSON.parse(localStorage.getItem(DRAFT_KEY));
      return d?.version === DRAFT_VERSION ? d : null;
    } catch { return null; }
  })();

  const [step, setStep] = useState(savedDraft?.step ?? 1);
  const [driverType, setDriverType] = useState(savedDraft?.driverType ?? '');
  const [form, setForm] = useState(savedDraft?.form ?? initialForm);
  const [employers, setEmployers] = useState(savedDraft?.employers ?? [emptyEmployer()]);
  const [policySignatures, setPolicySignatures] = useState(savedDraft?.policySignatures ?? {});
  const [errors, setErrors] = useState({});
  const [uploads, setUploads] = useState({ uploadingCdl: false, uploadingMed: false, uploadingVoidCheck: false });
  const [submitting, setSubmitting] = useState(false);
  const [user, setUser] = useState(null);
  const [existingProfile, setExistingProfile] = useState(null);

  const set = (k, v) => setForm(p => ({ ...p, [k]: v }));

  // Persist draft to localStorage whenever key state changes
  useEffect(() => {
    try {
      localStorage.setItem(DRAFT_KEY, JSON.stringify({ version: DRAFT_VERSION, step, driverType, form, employers, policySignatures }));
    } catch { /* storage full or unavailable */ }
  }, [step, driverType, form, employers, policySignatures]);

  useEffect(() => {
    client.auth.me().then(async (u) => {
      setUser(u);
      if (u) {
        const firstName = u.full_name?.split(' ')[0] || '';
        const lastName = u.full_name?.split(' ').slice(1).join(' ') || '';
        // Only prefill name/email if there's no saved draft (avoid overwriting user's work)
        if (!savedDraft) {
          setForm(p => ({ ...p, email: u.email || '', first_name: firstName, last_name: lastName, lic_first: firstName, lic_last: lastName }));
        }
        const profiles = await client.entities.DriverProfile.filter({ user_email: u.email });
        if (profiles.length > 0) setExistingProfile(profiles[0]);
      }
    });
  }, []);

  const validateStep = () => {
    const errs = {};
    if (step === 1) {
      if (!driverType) errs.driverType = 'Please select an application type';
    }
    if (step === 2) {
      if (!form.first_name) errs.first_name = 'Required';
      if (!form.last_name) errs.last_name = 'Required';
      if (!form.dob) errs.dob = 'Required';
      if (!form.ein) errs.ein = 'Required';
      if (!form.phone) errs.phone = 'Required';
      if (!form.email) errs.email = 'Required';
      if (!form.address_street) errs.address_street = 'Required';
      if (!form.address_city) errs.address_city = 'Required';
      if (!form.address_state) errs.address_state = 'Required';
      if (!form.address_zip) errs.address_zip = 'Required';
    }
    if (step === 3) {
      if (!form.lic_first) errs.lic_first = 'Required';
      if (!form.lic_last) errs.lic_last = 'Required';
      if (!form.cdl_number) errs.cdl_number = 'Required';
      if (!form.cdl_state) errs.cdl_state = 'Required';
      if (!form.cdl_issued_date) errs.cdl_issued_date = 'Required';
      if (!form.cdl_expiration_date) errs.cdl_expiration_date = 'Required';
      if (!form.med_card_issue_date) errs.med_card_issue_date = 'Required';
      if (!form.med_card_expiration_date) errs.med_card_expiration_date = 'Required';
      if (!form.cdl_document_url) errs.cdl_document_url = 'Please upload your CDL (front & back)';
      if (!form.med_card_document_url) errs.med_card_document_url = 'Please upload your Medical Card';
    }
    if (step === 5) {
      // All employers must have a name and start date
      const missing = employers.some(e => !e.name || !e.date_from);
      if (missing) {
        errs.employment_coverage = 'Each employer entry requires a name and start date.';
      } else {
        // Find the earliest start date across all employers
        const parseMMYYYY = (s) => {
          const [m, y] = (s || '').split('/').map(Number);
          return m && y ? new Date(y, m - 1) : null;
        };
        const dates = employers.map(e => parseMMYYYY(e.date_from)).filter(Boolean);
        const earliest = dates.length ? new Date(Math.min(...dates)) : null;
        const yearsCovered = earliest ? (Date.now() - earliest) / (1000 * 60 * 60 * 24 * 365.25) : 0;
        if (yearsCovered < 7) {
          const remaining = (7 - yearsCovered).toFixed(1);
          errs.employment_coverage = `Employment history must cover at least 7 years. You need ${remaining} more year(s) — add earlier employers.`;
        }
      }
    }
    if (step === 6) {
      if (driverType === 'Owner Operator') {
        if (!form.equipment_preference) errs.equipment_preference = 'Please select an option';
        if (form.equipment_preference === 'own') {
          if (!form.truck_make) errs.truck_make = 'Required';
          if (!form.truck_model) errs.truck_model = 'Required';
          if (!form.trailer_info) errs.trailer_info = 'Required';
          if (!form.truck_vin) errs.truck_vin = 'Required';
        }
      }
      if (!form.bank_routing_number) errs.bank_routing_number = 'Required';
      if (!form.bank_account_number) errs.bank_account_number = 'Required';
    }
    if (step === 7) {
      if (driverType === 'Owner Operator') {
        if (!form.w9_tax_classification) errs.w9_tax_classification = 'Required';
        if (!form.w9_tin || form.w9_tin.replace(/\D/g, '').length < 9) errs.w9_tin = 'Enter a valid SSN or EIN';
        if (!form.w9_signature) errs.w9_signature = 'Signature is required';
      } else {
        // SSN is always required (Form W-4 Step 1(b)), even when claiming
        // exemption -- exemption only skips filing status / Steps 2-4.
        if (!form.w4_ssn || form.w4_ssn.replace(/\D/g, '').length !== 9) errs.w4_ssn = 'Enter a valid 9-digit SSN';
        if (!form.w4_exempt && !form.w4_filing_status) errs.w4_filing_status = 'Select a filing status';
        if (!form.w4_signature) errs.w4_signature = 'Signature is required';
      }
    }
    if (step === 8) {
      // Was never actually validated -- an applicant could click Next
      // here having signed zero of the 5 required policy documents
      // (Clearinghouse consent, drug/alcohol testing policy, PSP
      // disclosure, FCRA background check authorization, safety
      // performance history) and go on to submit and be approved with
      // none of them on file. dqf_policies_consents (computed the same
      // way at submit time, below) isn't a required DQF item on its
      // own, so nothing downstream was catching this either.
      if (Object.keys(policySignatures).length < 5) {
        errs.policy_signatures = 'Please sign all 5 policy documents above before continuing.';
      }
    }
    if (step === 9) {
      if (!form.app_signature || form.app_signature.trim() === '') errs.app_signature = 'Signature is required';
    }
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handleNext = () => {
    if (!validateStep()) return;
    setStep(s => Math.min(s + 1, STEPS.length));
    window.scrollTo(0, 0);
  };

  const handleBack = () => {
    setStep(s => Math.max(s - 1, 1));
    window.scrollTo(0, 0);
  };

  const handleSignPolicy = (policyId, sig) => {
    setPolicySignatures(p => ({ ...p, [policyId]: sig }));
  };

  const generateAndUploadPdf = async () => {
    const companySettings = await client.entities.CompanySettings.list().catch(() => []);
    const companyName = companySettings.find(s => s.key === 'company_name')?.value || 'GF Trucklines LLC';
    // Was hardcoded "3%" regardless of what Accounting configured in Pay
    // & Deductions -- same fix as the live copy on this same step.
    const dispatchFeePercent = numOrDefault(
      companySettings.find(s => s.key === 'dispatch_fee_percent')?.value, 3
    );

    const doc = new jsPDF({ unit: 'pt', format: 'letter' });
    const W = 612, ml = 50, mr = 562;
    let y = 50;

    doc.setFontSize(16); doc.setFont('helvetica', 'bold');
    doc.text(`${companyName} — Driver Application`, W / 2, y, { align: 'center' }); y += 30;

    doc.setFontSize(10); doc.setFont('helvetica', 'normal');
    doc.text(`Submitted: ${new Date().toLocaleDateString()}`, ml, y); y += 20;

    const section = (title) => {
      doc.setFillColor(230, 230, 230);
      doc.rect(ml, y - 11, mr - ml, 16, 'F');
      doc.setFont('helvetica', 'bold'); doc.setFontSize(9);
      doc.text(title, ml + 2, y); y += 16;
      doc.setFont('helvetica', 'normal');
    };

    const row = (label, value) => {
      doc.setFontSize(9);
      doc.text(`${label}: ${value || '—'}`, ml, y); y += 13;
    };

    section('Personal Information');
    row('Name', `${form.first_name} ${form.last_name}`);
    row('Company', form.company_name);
    row('DOB', form.dob); row('EIN/SSN', form.ein);
    row('Phone', form.phone); row('Email', form.email);
    row('Address', `${form.address_street}, ${form.address_city}, ${form.address_state} ${form.address_zip}`);
    row('TWIC', form.twic_card); row('Passport', form.passport); y += 6;

    section("CDL Information");
    row('CDL Number', form.cdl_number); row('State', form.cdl_state);
    row('Class', form.cdl_class); row('Expires', form.cdl_expiration_date);
    row('Endorsements', form.cdl_endorsements); y += 6;

    section('Driving Record');
    row('Accidents (3yr)', form.accidents); row('Violations (3yr)', form.violations);
    row('Denied License', form.denied_license); row('Revoked License', form.revoked_license); y += 6;

    section('Employment History');
    employers.forEach((emp, i) => {
      row(`Employer ${i + 1}`, emp.name);
      row('  Dates', `${emp.date_from} - ${emp.is_present ? 'Present' : emp.date_to}`);
    }); y += 6;

    if (y > 650) { doc.addPage(); y = 50; }
    section('Equipment & Banking');
    row('Equipment', form.equipment_preference === 'own' ? 'Owner-Operator' : 'Lease from Carrier');
    if (form.equipment_preference === 'own') {
      row('Truck', form.truck_info); row('Trailer', form.trailer_info);
      row('VIN', form.truck_vin);
    }
    row('Dispatch Service', form.dispatch_service ? `Yes (${dispatchFeePercent}%)` : 'No');
    row('Routing #', form.bank_routing_number);
    row('Account #', form.bank_account_number ? '****' + form.bank_account_number.slice(-4) : '—'); y += 6;

    if (y > 650) { doc.addPage(); y = 50; }
    section('Tax Forms');
    if (driverType === 'Owner Operator') {
      row('Form on File', 'W-9');
      row('Tax Classification', form.w9_tax_classification);
      row('TIN Type', form.w9_tin_type);
      row('Signed', form.w9_signature ? 'Yes' : 'No');
    } else {
      row('Form on File', 'W-4');
      row('Filing Status', form.w4_exempt ? 'Exempt' : form.w4_filing_status);
      row('Signed', form.w4_signature ? 'Yes' : 'No');
    }
    y += 6;

    section('Policy Signatures');
    const policies = ['testing_policy', 'safety_performance', 'clearinghouse_consent', 'psp_disclosure', 'fair_credit'];
    policies.forEach(p => row(p.replace(/_/g, ' '), policySignatures[p] ? 'Signed ✓' : 'Not signed')); y += 10;

    if (form.app_signature && form.app_signature.startsWith('data:')) {
      doc.addImage(form.app_signature, 'PNG', ml, y, 100, 40);
    }
    doc.line(ml, y + 45, ml + 150, y + 45);
    doc.setFontSize(9); doc.text('Applicant Signature', ml, y + 56);
    doc.text(`Date: ${form.app_sign_date}`, ml + 230, y + 50);

    const blob = doc.output('blob');
    const file = new File([blob], `driver_application_${form.first_name}_${form.last_name}.pdf`, { type: 'application/pdf' });
    const { file_url } = await client.integrations.Core.UploadFile({ file });
    return file_url;
  };

  const handleSubmit = async () => {
    if (!validateStep()) return;
    setSubmitting(true);
    if (form.equipment_preference === 'own' && form.truck_vin) {
      const vinConflict = await findVinConflict(form.truck_vin, { excludeProfileId: existingProfile?.id });
      if (vinConflict) { toast.error(vinConflict); setSubmitting(false); return; }
    }
    toast.info('Submitting your application...');

    // Upload signature image separately if it's a drawn (data URL) signature
    let signatureUrl = form.app_signature;
    if (form.app_signature?.startsWith('data:')) {
      try {
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');
        const img = new Image();
        await new Promise((resolve) => {
          img.onload = () => {
            canvas.width = img.width;
            canvas.height = img.height;
            ctx.drawImage(img, 0, 0);
            canvas.toBlob(async (blob) => {
              const sigFile = new File([blob], 'signature.png', { type: 'image/png' });
              const { file_url } = await client.integrations.Core.UploadFile({ file: sigFile });
              signatureUrl = file_url;
              resolve();
            });
          };
          img.src = form.app_signature;
        });
      } catch {
        // keep original if upload fails
      }
    }

    const fullAddress = `${form.address_street}, ${form.address_city}, ${form.address_state} ${form.address_zip}`;
    const prevAddress = form.prev_address_street ? `${form.prev_address_street}, ${form.prev_address_city}, ${form.prev_address_state} ${form.prev_address_zip}` : '';

    // Map onboarding employers to PreviousEmploymentSection format so HR can work them immediately
    const prevEmployerData = employers.map(e => ({
      id: e.id,
      name: e.name || '',
      contact: e.contact || '',
      phone: e.phone || '',
      fax: e.fax || '',
      email: e.email || '',
      address: e.address || '',
      city: e.city || '',
      state: e.state || '',
      zip: e.zip || '',
      position: e.position || '',
      reason_left: e.reason || '',
      date_from: e.date_from || '',
      date_to: e.date_to || '',
      is_present: e.is_present || false,
      dot_regulated: e.dot_regulated || 'Yes',
      safety_sensitive: e.safety_sensitive || 'Yes',
      gaps: e.gaps || '',
      notes: '',
      documents: [],
      status: 'Pending',
      completed_date: '',
    }));

    // Step 1's Back button lets an applicant change driverType at any
    // point, including after filling in O/O-only equipment/dispatch
    // fields or Company-only-vs-O/O-only tax form fields on a later
    // step -- nothing ever cleared those when the type changed, so the
    // submitted profile could carry e.g. a real truck_vin and
    // dispatch_service: true on a profile saved as driver_type:
    // 'Company Driver'. Every field below that's meaningful to only one
    // type is now gated on the FINAL driverType at submit time, not on
    // whatever happened to be typed while a different type was
    // selected.
    const isOwnerOperator = driverType === 'Owner Operator';
    const profileData = {
      full_name: `${form.first_name} ${form.last_name}`,
      company_name: isOwnerOperator ? form.company_name : '',
      dob: form.dob,
      ein: form.ein,
      phone: form.phone,
      user_email: user?.email || form.email,
      address: fullAddress,
      prev_address: prevAddress,
      twic_card: form.twic_card,
      passport: form.passport,
      cdl_number: form.cdl_number,
      cdl_state: form.cdl_state,
      cdl_class: form.cdl_class,
      cdl_endorsements: form.cdl_endorsements,
      cdl_issued_date: form.cdl_issued_date,
      cdl_expiration_date: form.cdl_expiration_date,
      cdl_document_url: form.cdl_document_url,
      med_card_issue_date: form.med_card_issue_date,
      med_card_expiration_date: form.med_card_expiration_date,
      med_card_examiner_name: form.med_card_examiner_name,
      med_card_registry_number: form.med_card_registry_number,
      med_card_document_url: form.med_card_document_url,
      accidents: form.accidents,
      violations: form.violations,
      denied_license: form.denied_license,
      revoked_license: form.revoked_license,
      forfeiture_explain: form.forfeiture_explain,
      driver_type: driverType || (form.equipment_preference === 'own' ? 'Owner Operator' : 'Company Driver'),
      equipment_preference: isOwnerOperator ? form.equipment_preference : '',
      truck_type: isOwnerOperator ? form.truck_type : '',
      truck_year: isOwnerOperator ? form.truck_year : '',
      truck_make: isOwnerOperator ? form.truck_make : '',
      truck_model: isOwnerOperator ? form.truck_model : '',
      truck_info: isOwnerOperator ? ([form.truck_year, form.truck_make, form.truck_model].filter(Boolean).join(' ') || form.truck_info) : '',
      trailer_info: isOwnerOperator ? form.trailer_info : '',
      truck_vin: isOwnerOperator ? form.truck_vin : '',
      license_plate: isOwnerOperator ? form.license_plate : '',
      plate_state: isOwnerOperator ? form.plate_state : '',
      plate_expiration: isOwnerOperator ? form.plate_expiration : '',
      registration_expiration: isOwnerOperator ? form.registration_expiration : '',
      annual_inspection_due: isOwnerOperator ? form.annual_inspection_due : '',
      current_odometer: isOwnerOperator && form.current_odometer ? Number(form.current_odometer) : 0,
      dispatch_service: isOwnerOperator ? !!form.dispatch_service : false,
      bank_routing_number: form.bank_routing_number,
      bank_account_number: form.bank_account_number,
      void_check_url: form.void_check_url,
      // Tax form on file -- W-4 for Company Drivers (W-2 employees),
      // W-9 for Owner Operators (1099 contractors). Explicitly cleared
      // on the non-matching side (not just "left blank by the step
      // that never rendered them") so a driverType switch after
      // filling in the wrong side's tax form can't leave both sets on
      // the submitted profile.
      w4_ssn: isOwnerOperator ? '' : form.w4_ssn,
      w4_filing_status: isOwnerOperator ? '' : form.w4_filing_status,
      w4_multiple_jobs: isOwnerOperator ? '' : form.w4_multiple_jobs,
      w4_dependents_qualifying: isOwnerOperator ? '' : form.w4_dependents_qualifying,
      w4_dependents_other: isOwnerOperator ? '' : form.w4_dependents_other,
      w4_other_credits: isOwnerOperator ? '' : form.w4_other_credits,
      w4_other_income: isOwnerOperator ? '' : form.w4_other_income,
      w4_deductions: isOwnerOperator ? '' : form.w4_deductions,
      w4_extra_withholding: isOwnerOperator ? '' : form.w4_extra_withholding,
      w4_exempt: isOwnerOperator ? '' : form.w4_exempt,
      w4_signature: isOwnerOperator ? '' : form.w4_signature,
      w9_business_name: isOwnerOperator ? form.w9_business_name : '',
      w9_tax_classification: isOwnerOperator ? form.w9_tax_classification : '',
      w9_llc_classification: isOwnerOperator ? form.w9_llc_classification : '',
      w9_tin_type: isOwnerOperator ? form.w9_tin_type : '',
      w9_tin: isOwnerOperator ? form.w9_tin : '',
      w9_signature: isOwnerOperator ? form.w9_signature : '',
      app_signature: signatureUrl,
      app_sign_date: form.app_sign_date,
      dqf_driver_application: true,
      dqf_medical_card: !!form.med_card_document_url,
      dqf_alcohol_drug_statement: form.drug_test_positive === 'No',
      prev_employer_data: JSON.stringify(prevEmployerData),
      policy_signatures: JSON.stringify(policySignatures),
      dqf_policies_consents: Object.keys(policySignatures).length >= 5,
      onboarding_complete: true,
      approval_status: 'Pending',
    };

    try {
      if (existingProfile) {
        await client.entities.DriverProfile.update(existingProfile.id, profileData);
      } else {
        await client.entities.DriverProfile.create(profileData);
      }
      toast.success('Application submitted! We will review and be in touch.');
      localStorage.removeItem(DRAFT_KEY);
      // Generate and upload PDF after successful save (non-blocking)
      generateAndUploadPdf().then(pdfUrl => {
        if (pdfUrl && existingProfile) {
          client.entities.DriverProfile.update(existingProfile.id, { driver_application_url: pdfUrl });
        }
      }).catch(() => {});
      window.location.href = '/onboarding-success';
    } catch (err) {
      console.error('Submit error:', err);
      toast.error('Submission failed: ' + (err?.message || 'Please try again.'));
      setSubmitting(false);
    }
  };

  // Already submitted — block re-application.
  // onboarding_complete alone isn't reliable evidence of that: an admin
  // creating a driver record by hand (DriverFormModal.jsx's "Add Driver")
  // sets it true immediately, on the driver's behalf, to route that
  // record into the Approvals/DQF queue -- with no signature or any of
  // the wizard's other required fields ever filled in by the DRIVER.
  // A driver who then accepts an app invite for that same email hit this
  // block on their very first real visit here, telling them their
  // (nonexistent) application was "already submitted" with no way to
  // actually complete it. app_signature is set ONLY by this wizard's own
  // final step (never by the admin form), so its presence is what
  // actually means "the driver went through this and submitted it."
  if (existingProfile?.onboarding_complete && existingProfile?.app_signature && existingProfile?.approval_status === 'Pending') {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center px-4">
        <div className="text-center max-w-md space-y-4">
          <div className="w-16 h-16 rounded-full bg-amber-500/15 flex items-center justify-center mx-auto">
            <CheckCircle2 className="w-8 h-8 text-amber-400" />
          </div>
          <h1 className="font-space font-bold text-xl text-foreground">Application Already Submitted</h1>
          <p className="text-muted-foreground text-sm">
            Your application is under review. Our team will reach out once it has been processed. You cannot apply again while a submission is pending.
          </p>
          <button
            onClick={() => { window.location.href = '/landing'; }}
            className="text-xs text-primary underline hover:text-primary/80 transition-colors"
          >
            Return to home
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background py-8 px-4">
      <div className="max-w-2xl mx-auto">

        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="font-space font-bold text-2xl text-foreground">Driver Application</h1>
          <p className="text-muted-foreground text-sm mt-1">GF Trucklines LLC — Step {step} of {STEPS.length}</p>
        </div>

        {/* Step indicators */}
        <div className="flex items-center justify-center gap-1 mb-8 flex-wrap">
          {STEPS.map((s, i) => (
            <div key={s.id} className="flex items-center gap-1">
              <div className={cn(
                "w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold transition-all",
                step > s.id ? "bg-green-500 text-white" : step === s.id ? "bg-primary text-primary-foreground" : "bg-muted/60 text-muted-foreground"
              )}>
                {step > s.id ? <CheckCircle2 className="w-4 h-4" /> : s.id}
              </div>
              {i < STEPS.length - 1 && <div className={cn("w-6 h-0.5 rounded", step > s.id ? "bg-green-500" : "bg-white/10")} />}
            </div>
          ))}
        </div>
        <p className="text-center text-xs text-muted-foreground mb-6">{STEPS[step - 1].label}</p>

        {/* Step content */}
        <div className="glass-card p-6">
          {step === 1 && <StepDriverType selected={driverType} onSelect={setDriverType} />}
          {step === 2 && <StepPersonalInfo form={form} set={set} errors={errors} driverType={driverType} />}
          {step === 3 && <StepCDLMedical form={form} set={set} errors={errors} uploads={uploads} setUploads={setUploads} />}
          {step === 4 && <StepDrivingRecord form={form} set={set} />}
          {step === 5 && <StepEmploymentHistory employers={employers} setEmployers={setEmployers} errors={errors} />}
          {step === 6 && <StepEquipmentBanking form={form} set={set} errors={errors} uploads={uploads} setUploads={setUploads} driverType={driverType} />}
          {step === 7 && <StepTaxForms form={form} set={set} errors={errors} driverType={driverType} />}
          {step === 8 && <PolicyConsentStep signatures={policySignatures} onSign={handleSignPolicy} errors={errors} />}
          {step === 9 && <StepAppSignature form={form} set={set} errors={errors} />}
        </div>

        {/* Validation error for step 1 */}
        {step === 1 && errors.driverType && (
          <p className="text-sm text-destructive text-center mt-3">{errors.driverType}</p>
        )}

        {/* Navigation */}
        <div className="flex items-center justify-between mt-6">
          <Button variant="outline" onClick={handleBack} disabled={step === 1} className="border-border">
            <ChevronLeft className="w-4 h-4 mr-1" /> Back
          </Button>
          {step < STEPS.length ? (
            <Button onClick={handleNext} className="bg-primary text-primary-foreground">
              Next
              <ChevronRight className="w-4 h-4 ml-1" />
            </Button>
          ) : (
            <Button onClick={handleSubmit} disabled={submitting} className="bg-primary text-primary-foreground">
              {submitting ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Submitting...</> : 'Submit Application'}
            </Button>
          )}
        </div>

      </div>
    </div>
  );
}